/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  Divider,
  Flex,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { TiDelete } from "react-icons/ti";
import { toast } from "react-toastify";
import { AuthContext } from "../../Context/AuthProvider";
import { ConfirmDialog } from "../../hooks/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import LoadingSpinner from "../../LoadingSpinner";
import apiClient from "../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../utils/ApiClient/toastService";
import CustomModal from "../Common/CustomModal/CustomModal";
import AddNoteModalContent from "./Notes/AddNoteModalContent";

const NotesSection = ({ data }) => {
  const caseID = data?.ADMINISTRATIVE?.FIELDS?.id?.VALUE;
  const personID = data?.ADMINISTRATIVE?.FIELDS?.PERSON_ID?.VALUE;
  const [editNote, setEditNote] = useState(null);
  const { confirm, dialogProps } = useConfirmDialog();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const [notes, setNotes] = useState();
  const [selectedNote, setSelectedNote] = useState(null);
  const [newNoteText, setNewNoteText] = useState("");
  const [isNewNoteAdding, setIsNewNoteAdding] = useState(false);
  const { userData } = useContext(AuthContext);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setNotes(data?.CASE_NOTES?.FIELDS?.CASE_NOTES ?? []);
  }, [data]);

  const handleOpenModal = (note) => {
    setSelectedNote(note);
    onOpen();
  };

  const handleDelete = async (note) => {
    if (note.USER.VALUE !== userData?.userDetails) {
      toastService.warning("Only the owner of this note can delete it!");
      return;
    }

    const confirmed = await confirm({
      title: "Delete Note",
      message:
        "Are you sure you want to delete this note? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      const response = await apiClient.delete("/note", {
        params: {
          id: caseID,
          PERSON_ID: personID,
        },
        data: {
          NOTE: note.NOTE?.VALUE || "",
        },
      });

      setNotes((prev) => prev.filter((n) => n !== note));
      toastService.success(response.data);
    } catch (err) {
      console.error("Delete note error:", err);
    }
  };

  const handleAddNote = async () => {
    setIsNewNoteAdding(true);

    const noteText = newNoteText.trim();
    if (!noteText) {
      toastService.error("Note cannot be empty.");
      setIsNewNoteAdding(false);
      return;
    }

    try {
      const response = await apiClient.post(
        "/note",
        { NOTE: noteText },
        {
          params: {
            id: caseID,
            PERSON_ID: personID,
          },
          responseType: "text",
        }
      );

      const timestamp = response.data;
      if (!timestamp) throw new Error("Empty response");

      const newNote = {
        NOTE: { VALUE: noteText },
        USER: { VALUE: userData?.userDetails },
        MODIFIED_DATE: { VALUE: timestamp },
      };

      setNotes((prev) => [newNote, ...(prev || [])]);
      setNewNoteText("");
      onCreateClose();
      toastService.success("Note added.");
    } catch (err) {
      console.error("Add note error:", err);
      toastService.error("Failed to add note");
    } finally {
      setIsNewNoteAdding(false);
    }
  };

  const truncateText = useCallback((text, wordLimit = 7) => {
    const words = text.split(" ");
    return words.length <= wordLimit
      ? text
      : `${words.slice(0, wordLimit).join(" ")}...`;
  }, []);

  if (isDeleting) {
    return <LoadingSpinner />;
  }

  const handleEdit = (note) => {
    if (note.USER.VALUE !== userData?.userDetails) {
      return toast.warn("Only owner can edit this note!");
    }
    setEditNote(note);
    onEditOpen();
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text size="md" fontWeight={700} textAlign="center">
          Notes & Updates
        </Text>
        <Button colorScheme="teal" size="sm" onClick={onCreateOpen}>
          + Add Note
        </Button>
      </Flex>
      <Divider my={2} h="2px" bg="bgColor" />
      <VStack
        spacing={3}
        align="stretch"
        h="75vh"
        overflowY="scroll"
        scrollBehavior="smooth"
      >
        {notes
          ?.filter(
            (note) =>
              note?.USER?.VALUE?.trim() &&
              note?.MODIFIED_DATE?.VALUE?.trim() &&
              note?.NOTE?.VALUE?.trim()
          )
          .map((note, index) => {
            const fullText = note?.NOTE?.VALUE;
            const isLong = fullText?.split(" ").length > 7;

            return (
              <Box
                key={index}
                p={2}
                bg="gray.50"
                borderRadius="md"
                boxShadow="md"
                cursor="pointer"
              >
                <Flex alignItems="center" justify="space-between">
                  <Text fontSize="sm" color="black" fontWeight={500}>
                    --{note?.USER?.VALUE}
                  </Text>
                  {index === 0 && (
                    <Box display="flex" alignItems="center" gap={2}>
                      <FaEdit
                        cursor="pointer"
                        onClick={() => handleEdit(note)}
                      />
                      <TiDelete
                        onClick={() => handleDelete(note)}
                        cursor="pointer"
                        color="red"
                      />
                    </Box>
                  )}
                </Flex>
                <Text>
                  {truncateText(fullText)}{" "}
                  {isLong && (
                    <Button
                      onClick={() => handleOpenModal(note)}
                      colorScheme="blue"
                      variant="link"
                      size="xs"
                      bg="none"
                      border="none"
                    >
                      More
                    </Button>
                  )}
                </Text>
                <Flex alignItems="center" justifyContent="space-between" mt={2}>
                  <Text fontSize="sm" color="gray.600">
                    {moment(
                      note?.MODIFIED_DATE?.VALUE,
                      "MM/DD/YYYY HH:mm:ss"
                    ).format("MMMM D, YYYY [at] h:mm A") || "N/A"}
                  </Text>
                </Flex>
              </Box>
            );
          })}
      </VStack>

      <Divider my={3} />

      {/* View full note modal */}
      <CustomModal
        size="4xl"
        isCentered={true}
        isOpen={isOpen}
        onClose={onClose}
        bodyContent={<FullNotes selectedNote={selectedNote} />}
      />

      {/* Create new note modal */}
      <CustomModal
        size="xl"
        isCentered={true}
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        bodyContent={
          <AddNoteModalContent
            newNoteText={newNoteText}
            setNewNoteText={setNewNoteText}
            handleAddNote={handleAddNote}
            isNewNoteAdding={isNewNoteAdding}
          />
        }
      />
      {/* Create new note modal */}
      <CustomModal
        size="xl"
        isCentered={true}
        isOpen={isEditOpen}
        onClose={onEditClose}
        bodyContent={
          <UpdateNote
            editedNote={editNote}
            caseID={caseID}
            personID={personID}
            userData={userData}
            notes={notes}
            setNotes={setNotes}
            onCancel={onEditClose}
          />
        }
      />
      {/* confirm alert */}
      <ConfirmDialog dialogProps={dialogProps} />
    </Box>
  );
};

export default NotesSection;

const FullNotes = ({ selectedNote }) => {
  return (
    <Box h="30vh">
      <Flex justifyContent="space-between" mt={10}>
        <Text fontSize="xl" fontWeight={500}>
          --{selectedNote?.USER?.VALUE}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {moment(
            selectedNote?.MODIFIED_DATE?.VALUE,
            "MM/DD/YYYY HH:mm:ss"
          ).format("MMMM D, YYYY [at] h:mm A") || "N/A"}
        </Text>
      </Flex>
      <Divider variant="primary" h="2px" bg="gray.600" mt={2} />
      <Text mt={3}>{selectedNote?.NOTE?.VALUE}</Text>
    </Box>
  );
};

const UpdateNote = ({
  editedNote,
  caseID,
  personID,
  userData,
  notes,
  setNotes,
  onCancel,
}) => {
  const [noteText, setNoteText] = useState(editedNote?.NOTE?.VALUE || "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdateNote = () => {
    const trimmedNote = noteText.trim();
    if (!trimmedNote) {
      toast.error("Note cannot be empty.");
      return;
    }

    setIsUpdating(true);

    fetch(`/api/note?id=${caseID}&PERSON_ID=${personID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ NOTE: trimmedNote }),
    })
      .then(async (res) => {
        const data = await res.text();
        if (!res.ok || !data) throw new Error("Failed to update note");

        // ✅ Update the specific note in local state with server timestamp
        const updatedNotes = notes.map((n) =>
          n === editedNote
            ? {
                NOTE: { VALUE: trimmedNote },
                USER: { VALUE: userData?.userDetails },
                MODIFIED_DATE: { VALUE: data },
              }
            : n
        );

        setNotes(updatedNotes);
        toast.success("Note updated successfully.");
        onCancel();
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to update note");
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  return (
    <Box h="30vh">
      <Flex justifyContent="space-between" mt={4}>
        <Text fontSize="xl" fontWeight={500}>
          --{editedNote?.USER?.VALUE || "Unknown User"}
        </Text>
      </Flex>

      <Divider variant="primary" h="2px" bg="gray.600" mt={2} />

      <Textarea
        mt={3}
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder="Edit your note..."
      />

      <Flex justifyContent="flex-end" mt={4} gap={3}>
        <Button colorScheme="gray" onClick={onCancel} isDisabled={isUpdating}>
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleUpdateNote}
          isLoading={isUpdating}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
